(() => {
    'use strict';

    const DEBUG = 1;
    const nativeFetch = window.fetch.bind(window);

    /* ============================================================================
     * DUAL FILTER PRODUCT WIRING
     * -------------------------------------------------------------------------
     * be09 and be04 now each own a DISTINCT Shopify product/variant
     * (FILTER09 / FILTER04). Every place that used to reference a single
     * global `FILTER` constant has been changed to use `test.product`
     * instead. This also means ownership of a cart line ("which test added
     * this hidden filter item?") is now determined purely by matching
     * product_id/variant_id — no more hidden-property lookups needed, since
     * the two tests can never point at the same cart line by accident.
     * ========================================================================== */

    const FILTER04 = {
        productId: 9407989252342,
        variantId: 50751462047990,
        productHandle: 'bello%E2%84%A2-carbon-filter-subscription',
        handleVariants: ['bello™-carbon-filter-subscription', 'bello-carbon-filter-subscription'],
        titleKeyword: 'carbon filter subscription'
    };

    const FILTER09 = {
        productId: 9492102185206,
        variantId: 51441845731574,
        productHandle: 'bello%E2%84%A2-carbon-filter-subscription-335-days',
        handleVariants: ['bello™-carbon-filter-subscription-335-days', 'bello-carbon-filter-subscription-335-days'],
        titleKeyword: 'carbon filter subscription'
    };

    const SECTION_ID = 'template--21222776045814__main';

    /* ------------------------------- LOCALE --------------------------------- */

    const getLocale = () => {
        return window.Shopify?.locale === 'fr' ? 'fr' : 'en';
    };

    const getCopyForTest = (test) => test.copy[getLocale()] || test.copy.en;

    /* ---------------------------- TEST CONFIGS ---------------------------- */

    const TESTS = [
        {
            key: 'be09',
            className: 'be-09-test',
            offerClass: 'be-09-filter-upsell',
            testName: 'A/B Bello - [Filter Upsell In Cart on Discovery pack 09]',
            subscriptionPrice: 4410,
            subscriptionComparePrice: 4900,
            chargeDelayText: 'approximately 11 months',
            sellingPlanId: 7851081974,
            product: FILTER09,

            triggerProductIds: [9070096253174],
            triggerVariantIds: [48531029065974],
            triggerHandles: [
                'discovery-pack', 'bello-discovery-pack',
                'pack-decouverte', 'pack-découverte',
                'bello-pack-decouverte', 'bello-pack-découverte'
            ],
            triggerTitleKeywords: ['discovery pack', 'pack découverte', 'pack decouverte'],

            exclusionProductIds: [],
            exclusionVariantIds: [],
            exclusionHandles: [],
            exclusionTitleKeywords: [],

            image: { mode: 'static', url: 'https://cdn.shopify.com/s/files/1/0669/1382/9110/files/Image.jpg?v=1782821577' },
            titleFallback: {
                en: 'Bello™ Carbon Filter Subscription',
                fr: 'Abonnement au filtre au charbon Bello™'
            },

            copy: {
                en: {
                    heading: 'Never worry about replacing your filter',
                    sublineMuted: "Your first year's supply of filters is already included.",
                    subline: 'Your next filter ships automatically in 11 months.',
                    toggleTitle: (price) => `Subscribe &amp; auto-renew in 11 months - ${price}`,
                    toggleNote: 'Your next filter ships automatically in 11 months. Cancel anytime.',
                    subscribedTitle: (price) => `Subscribed - first charge of ${price} in 11 months`,
                    subscribedNote: (price) => `First charge: ${price} in approximately 11 months. Cancel anytime in your account.`
                },
                fr: {
                    heading: 'Ne vous souciez plus jamais de remplacer votre filtre',
                    sublineMuted: 'Votre stock de filtres pour la première année est déjà inclus.',
                    subline: 'Votre prochain filtre sera expédié automatiquement dans 11 mois.',
                    toggleTitle: (price) => `Abonnez-vous et renouvelez automatiquement dans 11 mois - ${price}`,
                    toggleNote: 'Votre prochain filtre sera expédié automatiquement dans 11 mois. Annulez en tout temps.',
                    subscribedTitle: (price) => `Abonné — premier paiement de ${price} dans environ 11 mois`,
                    subscribedNote: (price) => `Premier paiement : ${price} dans environ 11 mois. Annulez en tout temps.`
                }
            },

            css: `
        .be-09-test .be-09-filter-upsell { position: relative !important; margin: 18px 0 !important; width: 100% !important; box-sizing: border-box !important; }
        .be-09-test .be-09-subline, .be-09-test .be-09-price-row { display: none !important; }
        .be-09-test .kaktusc-cart__products+.be-09-filter-upsell { padding: 0 14px !important; }
        .be-09-test .be-09-filter-upsell.is-removing { opacity: 0 !important; transform: translateY(-6px) !important; transition: opacity 0.25s ease, transform 0.25s ease !important; }
        .be-09-test .be-09-heading { margin: 0 0 16px !important; color: #474E31; font-size: 20px; font-style: normal; font-weight: 400; line-height: 130%; letter-spacing: -0.8px; }
        .be-09-test .be-09-card { position: relative !important; display: grid !important; grid-template-columns: minmax(112px, 30%) 1fr !important; overflow: hidden !important; border-radius: 16px; border: 1px solid #3B382B; background: #fff !important; }
        .be-09-test .be-09-image-wrap { min-height: 168px !important; background: #f2f2f2 !important; }
        .be-09-test .be-09-image-wrap img { display: block !important; width: 100% !important; height: 100% !important; object-fit: cover !important; }
        .be-09-test .be-09-content { display: flex !important; flex-direction: column !important; align-items: flex-start !important; justify-content: center !important; gap: 6px !important; min-width: 0 !important; padding: 12px !important; box-sizing: border-box !important; }
        .be-09-test .be-09-rating { display: flex !important; align-items: center !important; gap: 8px !important; color: #333 !important; }
        .be-09-test .be-09-stars { color: #474E31 !important; white-space: nowrap !important; font-size: 12px; }
        .be-09-test .be-09-rating>span+span { color: #333; text-align: center; font-size: 12px; font-style: normal; font-weight: 400; line-height: 1; }
        .be-09-test .be-09-title, .be-09-test .be-09-subline { color: #3B382B !important; }
        .be-09-test .be-09-subline-muted { color: rgba(59, 56, 43, 0.60) !important; font-size: 12px; font-style: normal; font-weight: 300; line-height: 1; }
        .be-09-test .be-09-price { color: #474E31 !important; font-size: 14px; font-style: normal; font-weight: 700; line-height: 1; }
        .be-09-test .be-09-price-row { display: flex; align-items: center; gap: 6px; }
        .be-09-test .be-09-compare-price { color: #474E31 !important; text-decoration: line-through; font-size: 14px; font-style: normal; font-weight: 400; line-height: 1; }
        .be-09-test .be-09-title { color: #333; font-size: 16px; font-style: normal; font-weight: 600; line-height: 120%; letter-spacing: -0.3px; }
        .be-09-test .be-09-subline { font-size: 14px; font-style: normal; font-weight: 300; line-height: 1; }
        .be-09-test .be-09-toggle-row { display: grid; grid-template-columns: 50px 1fr; gap: 4px; align-items: flex-start; width: 100%; margin-top: 4px; padding: 0; border: 0; background: transparent; color: #333; text-align: left; cursor: pointer; }
        .be-09-test .be-09-toggle-row:disabled { cursor: wait !important; opacity: 0.7 !important; }
        .be-09-test .be-09-toggle { position: relative; width: 48px; height: 26px; border: 1px solid #474E31; border-radius: 13px; background: #fff; box-sizing: border-box; flex-shrink: 0; }
        .be-09-test .be-09-toggle-note font { display: block !important; }
        .be-09-test .be-09-toggle::before { content: ""; position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%; background: #474E31; opacity: 1; transition: opacity 0.2s ease, transform 0.2s ease; }
        .be-09-test .be-09-toggle-row.is-subscribed .be-09-toggle { background: #474E31; }
        .be-09-test .be-09-toggle-row.is-subscribed .be-09-toggle::before { opacity: 1; transform: translateX(22px); background: #fff; }
        .be-09-test .be-09-toggle-title { display: block; color: #3B382B !important; font-size: 14px; font-style: normal; font-weight: 600; line-height: 1.2; }
        .be-09-test .be-09-toggle-note { display: block; margin-top: 4px; color: #3B382B !important; font-size: 12px; font-style: normal; font-weight: 300; line-height: 1.25; letter-spacing: -0.1px; }
        @media screen and (max-width: 767px) {
          .be-09-test .be-09-title { font-size: 12px; }
          .be-09-test .be-09-heading { font-size: 18px; }
          .be-09-test .be-09-toggle-title { font-size: 12px; }
          .be-09-test .be-09-toggle-row { gap: 5px; }
          .be-09-test .be-09-content { padding: 6px !important; }
          .be-09-test .be-09-toggle-note { letter-spacing: -0.4px; }
        }
      `
        },
        {
            key: 'be04',
            className: 'be-04-test',
            offerClass: 'be-04-filter-upsell',
            testName: 'A/B Bello - [Filter Upsells In Cart 04]',
            subscriptionPrice: 4410,
            subscriptionComparePrice: 4900,
            chargeDelayText: 'approximately 5 months',
            sellingPlanId: 7851049206,
            product: FILTER04,

            triggerProductIds: [8917071757558, 8893198532854],
            triggerVariantIds: [47542537060598, 47441657528566],
            triggerHandles: [
                'bello-machine-eau-maison', 'starter-pack',
                'bello-wellness-pack', 'bello-pack-bien-etre'
            ],
            triggerTitleKeywords: [
                'starter pack', 'wellness pack',
                'pack de demarrage', 'pack de démarrage',
                'pack bien-être', 'pack bien-etre'
            ],

            exclusionProductIds: [],
            exclusionVariantIds: [],
            exclusionHandles: [
                'discovery-pack', 'bello-discovery-pack',
                'pack-decouverte', 'pack-découverte',
                'bello-pack-decouverte', 'bello-pack-découverte'
            ],
            exclusionTitleKeywords: ['discovery pack', 'pack découverte', 'pack decouverte'],

            image: { mode: 'dynamic' },
            titleFallback: {
                en: 'Bello™ Carbon Filter Subscription',
                fr: 'Abonnement au filtre au charbon Bello™'
            },

            copy: {
                en: {
                    heading: 'Never worry about replacing your filter',
                    sublineMuted: 'Your first filter is already included.',
                    subline: 'Your next filter ships automatically in 6 months.',
                    toggleTitle: (price) => `Subscribe &amp; auto-renew in 5 months - ${price}`,
                    toggleNote: 'Your next filter ships automatically. Cancel anytime.',
                    subscribedTitle: (price) => `Subscribed - first charge of ${price} in 5 months`,
                    subscribedNote: (price) => `First charge: ${price} in approximately 5 months. Cancel anytime in your account.`
                },
                fr: {
                    heading: 'Ne vous souciez plus jamais de remplacer votre filtre',
                    sublineMuted: 'Votre premier filtre est déjà inclus.',
                    subline: 'Votre prochain filtre est expédié automatiquement dans 6 mois.',
                    toggleTitle: (price) => `Abonnez-vous et renouvelez automatiquement dans 5 mois - ${price}`,
                    toggleNote: 'Votre prochain filtre est expédié automatiquement. Annulez à tout moment.',
                    subscribedTitle: (price) => `Abonné — premier paiement de ${price} dans 5 mois`,
                    subscribedNote: (price) => `Premier paiement : ${price} dans environ 5 mois. Annulez en tout temps dans votre compte.`
                }
            },

            css: `
        .be-04-test .be-04-filter-upsell { position: relative !important; margin: 18px 0 !important; width: 100% !important; box-sizing: border-box !important; }
        .be-04-test .be-04-subline, .be-04-test .be-04-price-row { display: none !important; }
        .be-04-test .kaktusc-cart__products+.be-04-filter-upsell { padding: 0 14px !important; }
        .be-04-test .be-04-filter-upsell.is-removing { opacity: 0 !important; transform: translateY(-6px) !important; transition: opacity 0.25s ease, transform 0.25s ease !important; }
        .be-04-test .be-04-heading { margin: 0 0 16px !important; color: #474E31; font-size: 20px; font-style: normal; font-weight: 400; line-height: 130%; letter-spacing: -0.8px; }
        .be-04-test .be-04-card { position: relative !important; display: grid !important; grid-template-columns: minmax(112px, 30%) 1fr !important; overflow: hidden !important; border-radius: 16px; border: 1px solid #474E31; background: #fff !important; }
        .be-04-test .be-04-image-wrap { min-height: 168px !important; background: #f2f2f2 !important; }
        .be-04-test .be-04-image-wrap img { display: block !important; width: 100% !important; height: 100% !important; object-fit: cover !important; }
        .be-04-test .be-04-content { display: flex !important; flex-direction: column !important; align-items: flex-start !important; justify-content: center !important; gap: 6px !important; min-width: 0 !important; padding: 12px !important; box-sizing: border-box !important; }
        .be-04-test .be-04-rating { display: flex !important; align-items: center !important; gap: 8px !important; color: #333 !important; }
        .be-04-test .be-04-stars { color: #474E31 !important; white-space: nowrap !important; font-size: 12px; }
        .be-04-test .be-04-rating>span+span { color: #333; text-align: center; font-size: 12px; font-style: normal; font-weight: 400; line-height: 1; }
        .be-04-test .be-04-title, .be-04-test .be-04-subline { color: #333 !important; }
        .be-04-test .be-04-subline-muted { color: rgba(59, 56, 43, 0.6) !important; font-size: 12px; font-style: normal; font-weight: 300; line-height: 1; }
        .be-04-test .be-04-price { color: #474E31 !important; font-size: 14px; font-style: normal; font-weight: 700; line-height: 1; }
        .be-04-test .be-04-price-row { display: flex; align-items: center; gap: 6px; }
        .be-04-test .be-04-compare-price { color: #474E31 !important; text-decoration: line-through; font-size: 14px; font-style: normal; font-weight: 400; line-height: 1; }
        .be-04-test .be-04-title { color: #333; font-size: 15px; font-style: normal; font-weight: 600; line-height: 120%; letter-spacing: -0.3px; }
        .be-04-test .be-04-subline { font-size: 14px; font-style: normal; font-weight: 300; line-height: 1; }
        .be-04-test .be-04-toggle-row { display: grid; grid-template-columns: 38px 1fr; gap: 10px; align-items: flex-start; width: 100%; margin-top: 4px; padding: 0; border: 0; background: transparent; color: #333; text-align: left; cursor: pointer; }
        .be-04-test .be-04-toggle-row:disabled { cursor: wait !important; opacity: 0.7 !important; }
        .be-04-test .be-04-toggle { position: relative; width: 34px; height: 20px; border: 1px solid #474E31; border-radius: 999px; background: #fff; box-sizing: border-box; flex-shrink: 0; }
        .be-04-test .be-04-toggle::before { content: ""; position: absolute; top: 3px; left: 3px; width: 12px; height: 12px; border-radius: 50%; background: #474E31; opacity: 1; transition: opacity 0.2s ease, transform 0.2s ease; }
        .be-04-test .be-04-toggle-row.is-subscribed .be-04-toggle { background: #474E31; }
        .be-04-test .be-04-toggle-row.is-subscribed .be-04-toggle::before { opacity: 1; transform: translateX(14px); background: #fff; }
        .be-04-test .be-04-toggle-title { display: block; color: #333; font-size: 14px; font-style: normal; font-weight: 600; line-height: 1.2; }
        .be-04-test .be-04-toggle-note { display: block; margin-top: 4px; color: #333; font-size: 12px; font-style: normal; font-weight: 300; line-height: 1.25; }
        @media screen and (max-width: 767px) {
          .be-04-test .be-04-title { font-size: 12px; }
          .be-04-test .be-04-heading { font-size: 18px; }
          .be-04-test .be-04-toggle-title { font-size: 12px; }
          .be-04-test .be-04-toggle-row { gap: 5px; }
        }
      `
        }
    ];

    // Hides EACH test's own filter line-item from the visible cart UI.
    // Built from TESTS so both FILTER04's and FILTER09's IDs are covered —
    // this was previously broken because it referenced an undefined global
    // `FILTER` object that no longer exists now that there are two products.
    const SHARED_HIDE_FILTER_LINE_CSS = (() => {
        const idSelectors = TESTS.flatMap((test) => [
            `.kaktusc-cart__product[data-id="${test.product.variantId}"]`,
            `.kaktusc-cart__product[data-product-id="${test.product.productId}"]`
        ]);

        const handleSelectors = Array.from(new Set(
            TESTS.flatMap((test) => test.product.handleVariants)
        )).flatMap((handle) => [
            `.cart-item:has(a[href*="${handle}"])`,
            `tr:has(a[href*="${handle}"])`
        ]);

        const allSelectors = [
            ...idSelectors,
            '.cart-item:has(a[href*="bello%E2%84%A2-carbon-filter-subscription"])',
            'tr:has(a[href*="bello%E2%84%A2-carbon-filter-subscription"])',
            ...handleSelectors
        ];

        return `${allSelectors.join(',\n    ')} {\n      display: none !important;\n    }\n  `;
    })();

    /* ------------------------------ HELPERS -------------------------------- */

    const debugLog = (...args) => { if (DEBUG) console.log('[bello-upsell]', ...args); };
    const debugSeen = new Set();
    const debugOnce = (key, ...args) => {
        if (debugSeen.has(key)) return;
        debugSeen.add(key);
        debugLog(...args);
    };

    const waitForElement = (selector, root = document) => new Promise((resolve) => {
        const found = root.querySelector(selector);
        if (found) { resolve(found); return; }
        const observer = new MutationObserver(() => {
            const el = root.querySelector(selector);
            if (!el) return;
            observer.disconnect();
            resolve(el);
        });
        observer.observe(root === document ? document.documentElement : root, { childList: true, subtree: true });
    });

    const escapeHtml = (value) => String(value || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

    const formatMoney = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
    const formatDisplayMoney = (cents) => (
        getLocale() === 'fr'
            ? `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} $`
            : formatMoney(cents)
    );

    const normalizeImageUrl = (url) => (url && url.indexOf('//') === 0 ? `${window.location.protocol}${url}` : (url || ''));

    const detectLocaleRootFromUrl = () => {
        const htmlLang = (document.documentElement.lang || '').toLowerCase();
        if (!htmlLang) return null;

        const primaryLang = htmlLang.split('-')[0];
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const first = (pathSegments[0] || '').toLowerCase();
        const firstPrimary = first.split('-')[0];

        if (first && (first === htmlLang || firstPrimary === primaryLang)) {
            return `/${pathSegments[0]}/`;
        }
        return null;
    };

    const getLocaleRoot = () => {
        const urlLocaleRoot = detectLocaleRootFromUrl();
        if (urlLocaleRoot) return urlLocaleRoot;

        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
    };

    // Locale-aware product URLs, now built PER TEST since each test points
    // at its own product handle (test.product.productHandle).
    const getProductUrl = (test) => `${getLocaleRoot()}products/${test.product.productHandle}.js`;
    const getProductPageUrl = (test) => `${getLocaleRoot()}products/${test.product.productHandle}`;

    /* ------------------------- CART / PRODUCT DATA -------------------------- */
    // Cached by product handle so two tests sharing the same handle only
    // trigger one network request between them, while tests with distinct
    // handles fetch independently.

    const productDataCache = new Map(); // handle -> Promise<productData>

    const getFallbackProductData = (test) => ({
        id: test.product.productId,
        _isFallback: true,
        title: test.titleFallback.en,
        handle: 'bello™-carbon-filter-subscription',
        available: true,
        price: 0,
        compare_at_price: test.subscriptionComparePrice,
        featured_image: '//cdn.shopify.com/s/files/1/0669/1382/9110/files/Filter_-min.jpg?v=1744838512',
        images: ['//cdn.shopify.com/s/files/1/0669/1382/9110/files/Filter_-min.jpg?v=1744838512'],
        media: [{ media_type: 'image', src: 'https://cdn.shopify.com/s/files/1/0669/1382/9110/files/Filter_-min.jpg?v=1744838512' }],
        variants: [{ id: test.product.variantId, available: true, price: 0, compare_at_price: test.subscriptionComparePrice }]
    });

    const fetchProductDataForTest = (test) => {
        const handle = test.product.productHandle;

        if (productDataCache.has(handle)) return productDataCache.get(handle);

        const request = nativeFetch(getProductUrl(test), { credentials: 'same-origin' })
            .then((r) => { if (!r.ok) throw new Error('Filter product request failed'); return r.json(); })
            .catch((error) => {
                debugLog('product data failed, using fallback', test.key, error);
                productDataCache.delete(handle);
                return getFallbackProductData(test);
            });

        productDataCache.set(handle, request);
        return request;
    };

    // Finds the variant belonging to THIS test inside a fetched product
    // response. Warns once if the configured variant isn't actually present
    // on the live product — that's a config mismatch, not a script bug.
    const getProductVariantForTest = (product, test) => {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const match = variants.find((v) => Number(v.id) === Number(test.product.variantId));

        if (!match && !product?._isFallback) {
            debugOnce(
                `variant-missing-${test.key}`,
                `⚠️ Configured variant for "${test.key}" (${test.product.variantId}) not found on fetched product "${test.product.productHandle}" — check the product/variant IDs in TESTS`
            );
        }

        return match || null;
    };

    const getRequestUrl = (request) => (typeof request === 'string' ? request : (request?.url || ''));

    const isCartRequestUrl = (request) => {
        const url = getRequestUrl(request);
        if (!url) return false;
        try {
            const path = new URL(url, window.location.origin).pathname;
            return /\/cart(?:\.js|\/add|\/change|\/update|\/clear)(?:\.js)?$/.test(path);
        } catch { return false; }
    };

    const isCartJsonRequestUrl = (request) => {
        const url = getRequestUrl(request);
        if (!url) return false;
        try {
            return /\/cart\.js$/.test(new URL(url, window.location.origin).pathname);
        } catch { return false; }
    };

    const cacheObservedCart = (cart) => {
        if (!cart || !Array.isArray(cart.items)) return;
        observedCart = cart;
        observedCartUpdatedAt = Date.now();
    };

    let observedCart = null;
    let observedCartUpdatedAt = 0;
    let isCartRequestObserverSetup = false;
    let cartRequest = null;

    const getRecentObservedCart = () => {
        if (!observedCart || Date.now() - observedCartUpdatedAt > 1500) return null;
        return observedCart;
    };

    const scheduleObservedCartRender = () => window.setTimeout(queueRenderAllRoots, 250);

    const setupCartRequestObserver = () => {
        if (isCartRequestObserverSetup) return;
        isCartRequestObserverSetup = true;

        window.fetch = (...args) => nativeFetch(...args).then((response) => {
            if (isCartJsonRequestUrl(args[0])) {
                response.clone().json().then(cacheObservedCart).catch(() => { }).finally(scheduleObservedCartRender);
            } else if (isCartRequestUrl(args[0])) {
                scheduleObservedCartRender();
            }
            return response;
        });

        if (!window.XMLHttpRequest?.prototype) return;
        const nativeXhrOpen = window.XMLHttpRequest.prototype.open;
        const nativeXhrSend = window.XMLHttpRequest.prototype.send;

        window.XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
            this.be_observedCartRequest = isCartRequestUrl(url);
            this.be_observedCartJsonRequest = isCartJsonRequestUrl(url);
            return nativeXhrOpen.call(this, method, url, ...rest);
        };

        window.XMLHttpRequest.prototype.send = function send(...args) {
            if (this.be_observedCartRequest) {
                this.addEventListener('loadend', () => {
                    if (this.be_observedCartJsonRequest) {
                        try {
                            cacheObservedCart(this.responseType === 'json' ? this.response : JSON.parse(this.responseText));
                        } catch (error) { debugLog('observed xhr cart json parse failed', error); }
                    }
                    scheduleObservedCartRender();
                }, { once: true });
            }
            return nativeXhrSend.apply(this, args);
        };
    };

    const fetchCart = () => {
        const recent = getRecentObservedCart();
        if (recent) return Promise.resolve(recent);
        if (cartRequest) return cartRequest;

        cartRequest = nativeFetch(`${getLocaleRoot()}cart.js`, { credentials: 'same-origin' })
            .then((r) => { if (!r.ok) throw new Error('Cart request failed'); return r.json(); })
            .then((cart) => { cacheObservedCart(cart); return cart; })
            .catch((error) => { debugLog('cart request failed', error); return null; })
            .finally(() => { cartRequest = null; });

        return cartRequest;
    };

    const fetchFreshCart = () => nativeFetch(`${getLocaleRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
    }).then((r) => { if (!r.ok) throw new Error(`Cart refresh failed: ${r.status}`); return r.json(); })
        .then((cart) => { cacheObservedCart(cart); return cart; });

    /* --------------------------- ITEM CLASSIFIERS --------------------------- */

    const getItemHandle = (item) => String(item?.handle || item?.product_handle || '').toLowerCase();
    const getItemTitle = (item) => `${item?.product_title || ''} ${item?.title || ''}`.toLowerCase();

    // ID-based ONLY (not handle/title) — both filter configs share the same
    // placeholder handle/title text, so those can't disambiguate which test
    // a line item belongs to. product_id/variant_id are guaranteed distinct
    // between FILTER04 and FILTER09, so they're the single source of truth
    // for per-test ownership.
    const isFilterCartItemForTest = (item, test) => {
        const productId = Number(item?.product_id);
        const variantId = Number(item?.variant_id || item?.id);
        return productId === test.product.productId || variantId === test.product.variantId;
    };

    const isTriggerCartItem = (item, test) => {
        const productId = Number(item?.product_id);
        const variantId = Number(item?.variant_id || item?.id);
        const handle = getItemHandle(item);
        const title = getItemTitle(item);
        return test.triggerProductIds.includes(productId)
            || test.triggerVariantIds.includes(variantId)
            || test.triggerHandles.includes(handle)
            || test.triggerTitleKeywords.some((kw) => title.indexOf(kw) !== -1);
    };

    const isExcludedCartItem = (item, test) => {
        const productId = Number(item?.product_id);
        const variantId = Number(item?.variant_id || item?.id);
        const handle = getItemHandle(item);
        const title = getItemTitle(item);
        return test.exclusionProductIds.includes(productId)
            || test.exclusionVariantIds.includes(variantId)
            || test.exclusionHandles.includes(handle)
            || test.exclusionTitleKeywords.some((kw) => title.indexOf(kw) !== -1);
    };

    const getCartStateForTest = (cart, test) => {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        return {
            hasTriggerProduct: items.some((item) => isTriggerCartItem(item, test)),
            hasFilterProduct: items.some((item) => isFilterCartItemForTest(item, test)),
            hasExcludedProduct: items.some((item) => isExcludedCartItem(item, test))
        };
    };

    const isAnyTriggerCartItem = (item) => TESTS.some((test) => isTriggerCartItem(item, test));

    const isFilterSubscribedForTest = (cart, test) => {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        return items.some((item) => isFilterCartItemForTest(item, test));
    };

    const isFilterDomProductForTest = (productEl, test) => Number(productEl?.dataset?.id) === Number(test.product.variantId);

    const isTriggerDomProduct = (productEl, test) => {
        const variantId = Number(productEl?.dataset?.id);
        const title = String(productEl?.textContent || '').toLowerCase();
        return test.triggerVariantIds.includes(variantId)
            || test.triggerTitleKeywords.some((kw) => title.indexOf(kw) !== -1);
    };

    const isExcludedDomProduct = (productEl, test) => {
        const variantId = Number(productEl?.dataset?.id);
        const title = String(productEl?.textContent || '').toLowerCase();
        return test.exclusionVariantIds.includes(variantId)
            || test.exclusionTitleKeywords.some((kw) => title.indexOf(kw) !== -1);
    };

    const getRootDomStateForTest = (rootDoc, test) => {
        const kaktusProducts = Array.from(rootDoc.querySelectorAll('.kaktusc-cart__product'));
        return {
            hasTriggerProduct: kaktusProducts.some((el) => isTriggerDomProduct(el, test)),
            hasFilterProduct: kaktusProducts.some((el) => isFilterDomProductForTest(el, test)),
            hasExcludedProduct: kaktusProducts.some((el) => isExcludedDomProduct(el, test))
        };
    };

    /* ------------------------------ ROOT/IFRAME ----------------------------- */

    const getRootLabel = (rootDoc = document) => {
        if (rootDoc === document) return 'parent';
        const frame = Array.from(document.querySelectorAll('iframe')).find((iframe) => {
            try { return iframe.contentDocument === rootDoc || iframe.contentWindow?.document === rootDoc; }
            catch { return false; }
        });
        return frame ? `iframe#${frame.id || 'no-id'}` : 'iframe/unknown';
    };

    const isKaktusFrameDocument = (frameDocument) => Boolean(
        frameDocument?.documentElement && (
            frameDocument.getElementById('kaktusc-root')
            || frameDocument.querySelector('.kaktusc-cart__products')
            || frameDocument.querySelector('.kaktusc-cart__wrap')
        )
    );

    const getAccessibleFrameDocuments = () => {
        const frameDocuments = [];
        document.querySelectorAll('iframe').forEach((iframe) => {
            try {
                const frameDocument = iframe.contentDocument || iframe.contentWindow?.document;
                const isCandidate = iframe.id === 'kaktusc-widget' || isKaktusFrameDocument(frameDocument);
                if (isCandidate && frameDocument?.documentElement && !frameDocuments.includes(frameDocument)) {
                    frameDocuments.push(frameDocument);
                }
            } catch (error) {
                if (iframe.id === 'kaktusc-widget') debugOnce('kaktus-frame-inaccessible', 'kaktus frame not accessible yet', error);
            }
        });
        return frameDocuments;
    };

    const getRootDocuments = () => [document, ...getAccessibleFrameDocuments()];
    const getKaktusDocuments = () => getAccessibleFrameDocuments().filter((d) => d.querySelector('.kaktusc-cart__products'));

    const setupFrameLoadListeners = () => {
        document.querySelectorAll('iframe#kaktusc-widget, iframe').forEach((iframe) => {
            if (iframe.dataset.beFrameListener === '1') return;
            iframe.dataset.beFrameListener = '1';
            iframe.addEventListener('load', () => window.setTimeout(queueRenderAllRoots, 100));
        });
    };

    // The offer also lands in #main-cart-items, which is the cart PAGE and lives in the main
    // document. Skipping that document left the block there with no styles at all: plain
    // heading, image at natural size, overflowing the column. The id guard below already
    // stops it being added twice, so the main document is no longer an exception.
    const injectRootStyle = (rootDoc) => {
        if (rootDoc.getElementById('bello-upsell-iframe-style')) return;
        const style = rootDoc.createElement('style');
        style.id = 'bello-upsell-iframe-style';
        style.textContent = SHARED_HIDE_FILTER_LINE_CSS + TESTS.map((t) => t.css).join('\n');
        rootDoc.head?.appendChild(style);
    };

    const getInsertionTargets = (rootDoc = document) => {
        const selectors = ['.kaktusc-cart__products', '#CartDrawer-CartItems', '#main-cart-items'];
        const targets = [];
        selectors.forEach((selector) => {
            rootDoc.querySelectorAll(selector).forEach((target) => {
                if (!targets.includes(target)) targets.push(target);
            });
        });
        return targets;
    };

    let targetIndex = 0;
    const getTargetId = (target) => {
        if (!target.dataset.beTargetId) {
            targetIndex += 1;
            target.dataset.beTargetId = `be-target-${targetIndex}`;
        }
        return target.dataset.beTargetId;
    };

    /* -------------------------------- OFFERS -------------------------------- */

    const removeOffers = (test, animate = false, rootDoc = document) => {
        rootDoc.querySelectorAll(`.${test.offerClass}`).forEach((offer) => {
            if (!animate) { offer.remove(); return; }
            offer.classList.add('is-removing');
            window.setTimeout(() => offer.remove(), 250);
        });
    };

    const removeStaleOffers = (targets, test, rootDoc = document) => {
        const targetIds = targets.map(getTargetId);
        rootDoc.querySelectorAll(`.${test.offerClass}`).forEach((offer) => {
            if (!targetIds.includes(offer.getAttribute('data-be-target'))) offer.remove();
        });
    };

    const getOfferImage = (test, product) => (
        test.image.mode === 'static'
            ? test.image.url
            : normalizeImageUrl(product?.media?.find((m) => m.media_type === 'image')?.src || product?.featured_image || product?.images?.[0])
    );

    const getPrefix = (test) => test.className.replace('-test', '');

    const buildOfferHtml = (test, product) => {
        const prefix = getPrefix(test);
        const copy = getCopyForTest(test);
        const image = getOfferImage(test, product);
        const title =
            test.titleFallback[getLocale()] ||
            test.titleFallback.en ||
            product?.title ||
            '';
        const comparePrice = formatMoney(test.subscriptionComparePrice);
        const subscriptionPrice = formatDisplayMoney(test.subscriptionPrice);

        return `
      <div class="${test.offerClass}" data-be-target="">
        <h3 class="${prefix}-heading">${copy.heading}</h3>
        <div class="${prefix}-card">
          <div class="${prefix}-image-wrap">
            <img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">
          </div>
          <div class="${prefix}-content">
            <div class="${prefix}-rating" aria-label="Rated 4.8 out of 5 from 32 reviews">
              <span class="${prefix}-stars" aria-hidden="true">★★★★★</span>
              <span>4.8 (32)</span>
            </div>
            <div class="${prefix}-title">${escapeHtml(title)}</div>
            <div class="${prefix}-subline-muted">${copy.sublineMuted}</div>
            <div class="${prefix}-subline">${copy.subline}</div>
            <div class="${prefix}-price-row">
              <span class="${prefix}-compare-price">${comparePrice}</span>
              <span class="${prefix}-price">${subscriptionPrice}</span>
            </div>
            <button class="${prefix}-toggle-row bello-subscription-toggle" type="button" aria-pressed="false" data-test-key="${test.key}" data-be-subscribed="false">
              <span class="${prefix}-toggle" aria-hidden="true"></span>
              <span class="${prefix}-toggle-copy">
                <span class="${prefix}-toggle-title" data-be-role="toggle-title">${copy.toggleTitle(subscriptionPrice)}</span>
                <span class="${prefix}-toggle-note" data-be-role="toggle-note">${copy.toggleNote}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    `;
    };

    const setOfferSubscribedState = (test, offer, isSubscribed) => {
        const button = offer?.querySelector?.('.bello-subscription-toggle');
        if (!button) return;
        const stateValue = isSubscribed ? 'true' : 'false';

        if (button.dataset.beSubscribed !== stateValue) button.dataset.beSubscribed = stateValue;
        if (button.getAttribute('aria-pressed') !== stateValue) button.setAttribute('aria-pressed', stateValue);
        if (button.classList.contains('is-subscribed') !== isSubscribed) button.classList.toggle('is-subscribed', isSubscribed);

        const copy = getCopyForTest(test);
        const titleEl = button.querySelector('[data-be-role="toggle-title"]');
        const noteEl = button.querySelector('[data-be-role="toggle-note"]');
        const price = formatDisplayMoney(test.subscriptionPrice);
        const nextTitle = isSubscribed ? copy.subscribedTitle(price) : copy.toggleTitle(price);
        const nextNote = isSubscribed ? copy.subscribedNote(price) : copy.toggleNote;

        if (titleEl && titleEl.innerHTML !== nextTitle) titleEl.innerHTML = nextTitle;
        if (noteEl && noteEl.innerHTML !== nextNote) noteEl.innerHTML = nextNote;
    };

    const syncOfferSubscribedState = (test, isSubscribed, rootDoc = document) => {
        rootDoc.querySelectorAll(`.${test.offerClass}`).forEach((offer) => setOfferSubscribedState(test, offer, isSubscribed));
    };

    const insertOffers = (targets, test, product, isSubscribed = false) => {
        const offerHtml = buildOfferHtml(test, product);
        targets.forEach((target) => {
            target.ownerDocument.body?.classList.add(test.className);
            const targetId = getTargetId(target);
            const existingOffer = target.ownerDocument.querySelector(`.${test.offerClass}[data-be-target="${targetId}"]`);
            if (existingOffer) { setOfferSubscribedState(test, existingOffer, isSubscribed); return; }

            target.insertAdjacentHTML('afterend', offerHtml);
            const insertedOffer = target.nextElementSibling;
            if (insertedOffer?.classList.contains(test.offerClass)) {
                insertedOffer.setAttribute('data-be-target', targetId);
                setOfferSubscribedState(test, insertedOffer, isSubscribed);
            }
        });
    };

    /* ------------------------------ CART SECTIONS ---------------------------- */

    const getSectionIds = () => {
        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const cartItemsSection = document.querySelector('#main-cart-items')?.dataset.id;
        const cartFooterSection = document.querySelector('#main-cart-footer')?.dataset.id;
        [cartItemsSection, cartFooterSection].forEach((sectionId) => {
            if (sectionId && !sections.includes(sectionId)) sections.push(sectionId);
        });
        return sections;
    };

    const replaceSectionHtml = (sectionId, html) => {
        if (!sectionId || !html) return;
        const currentSection = document.getElementById(`shopify-section-${sectionId}`);
        if (!currentSection) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newSection = doc.getElementById(`shopify-section-${sectionId}`);
        if (newSection) currentSection.innerHTML = newSection.innerHTML;
    };

    const updateCartSections = (sections) => {
        if (!sections) return;

        const drawerHtml = sections['cart-drawer'];
        if (drawerHtml) {
            const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
            const newDrawer = doc.querySelector('cart-drawer') || doc.querySelector('#CartDrawer');
            const currentDrawer = document.querySelector('cart-drawer') || document.querySelector('#CartDrawer');
            if (newDrawer && currentDrawer) currentDrawer.innerHTML = newDrawer.innerHTML;
        }

        const bubbleHtml = sections['cart-icon-bubble'];
        if (bubbleHtml) {
            const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
            const newBubble = doc.querySelector('#cart-icon-bubble');
            const currentBubble = document.querySelector('#cart-icon-bubble');
            if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;
        }

        getSectionIds().forEach((sectionId) => replaceSectionHtml(sectionId, sections[sectionId]));
    };

    const syncKaktusCartFromCart = (cart) => {
        const kaktusDocuments = getKaktusDocuments();

        kaktusDocuments.forEach((rootDoc) => {
            const totalEl = rootDoc.querySelector('.kaktusc-cart__total-price');
            if (totalEl && Number.isFinite(Number(cart?.total_price))) totalEl.textContent = formatMoney(cart.total_price);
            TESTS.forEach((test) => syncOfferSubscribedState(test, isFilterSubscribedForTest(cart, test), rootDoc));
        });

        TESTS.forEach((test) => syncOfferSubscribedState(test, isFilterSubscribedForTest(cart, test), document));
    };

    const dispatchCartRefreshEvents = (cart = null) => {
        const detail = cart ? { detail: { cart } } : {};
        window.dispatchEvent(new CustomEvent('cart:refresh', detail));
        window.dispatchEvent(new CustomEvent('cart:updated', detail));
        window.dispatchEvent(new CustomEvent('ig:updateCart', detail));
    };

    const schedulePostAddKaktusSync = (cart) => {
        [120, 650, 1400].forEach((delay) => window.setTimeout(() => syncKaktusCartFromCart(cart), delay));
    };

    const getCartAddUrl = () => `${getLocaleRoot()}cart/add`;
    const getCartChangeUrl = () => `${getLocaleRoot()}cart/change`;

    const afterSubscriptionCartChange = (data) => {
        updateCartSections(data?.sections);
        return fetchFreshCart().then((cart) => {
            syncKaktusCartFromCart(cart);
            dispatchCartRefreshEvents(cart);
            schedulePostAddKaktusSync(cart);
            return cart;
        });
    };

    let isAdding = false;

    const addSubscriptionToCart = (test) => {
        if (!test.sellingPlanId) {
            debugLog(`No selling plan configured for test "${test.key}" — aborting subscription add`);
            return Promise.reject(new Error(`Missing selling plan for test ${test.key}`));
        }

        const formData = new FormData();
        const isFrench = getLocale() === 'fr';
        const displayPrice = formatDisplayMoney(test.subscriptionPrice);
        formData.append('form_type', 'product');
        formData.append('utf8', '✓');
        formData.append('id', String(test.product.variantId));
        formData.append('quantity', '1');
        formData.append('product-id', String(test.product.productId));
        formData.append('section-id', SECTION_ID);
        formData.append('sections', getSectionIds().join(','));
        formData.append('sections_url', getProductPageUrl(test));
        formData.append('selling_plan', String(test.sellingPlanId));
        // Kept for admin/order visibility only — ownership itself is now
        // determined by product/variant ID, not this property.
        formData.append(`properties[_${test.key}_hidden_subscription]`, 'true');

        if (isFrench) {
            const delayText = test.key === 'be09' ? 'dans environ 11 mois' : 'dans environ 5 mois';
            //formData.append('properties[Premier paiement]', `${displayPrice} ${delayText}`);
            //formData.append('properties[Annulez en tout temps]', 'Annulez en tout temps');
        } else {
            //formData.append('properties[First charge]', `${displayPrice} in ${test.chargeDelayText}`);
            //formData.append('properties[Cancel anytime]', 'Cancel anytime in your account.');
        }

        return nativeFetch(getCartAddUrl(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
            credentials: 'same-origin'
        }).then((r) => { if (!r.ok) throw new Error('Subscription add to cart failed'); return r.json(); });
    };

    const removeSubscriptionFromCart = (cart, test) => {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const lineIndex = items.findIndex((item) => isFilterCartItemForTest(item, test)) + 1;
        if (!lineIndex) return Promise.resolve({ sections: null });

        const formData = new FormData();
        formData.append('line', String(lineIndex));
        formData.append('quantity', '0');
        formData.append('sections', getSectionIds().join(','));
        formData.append('sections_url', window.location.pathname);

        return nativeFetch(getCartChangeUrl(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
            credentials: 'same-origin'
        }).then((r) => { if (!r.ok) throw new Error('Subscription remove from cart failed'); return r.json(); });
    };

    const toggleSubscriptionInCart = (button, test) => {
        if (isAdding) return;
        const offer = button.closest(`.${test.offerClass}`);
        isAdding = true;
        button.disabled = true;
        offer?.classList.add('is-adding');

        fetchFreshCart()
            .then((cart) => {
                const hasFilterItem = isFilterSubscribedForTest(cart, test);
                getRootDocuments().forEach((rootDoc) => syncOfferSubscribedState(test, !hasFilterItem, rootDoc));
                return hasFilterItem ? removeSubscriptionFromCart(cart, test) : addSubscriptionToCart(test);
            })
            .then(afterSubscriptionCartChange)
            .then((cart) => {
                getRootDocuments().forEach((rootDoc) => syncOfferSubscribedState(test, isFilterSubscribedForTest(cart, test), rootDoc));
            })
            .catch((error) => {
                debugLog('subscription toggle failed', error);
                fetchFreshCart().then((cart) => {
                    getRootDocuments().forEach((rootDoc) => syncOfferSubscribedState(test, isFilterSubscribedForTest(cart, test), rootDoc));
                }).catch((e) => debugLog('resync after failure failed', e));
            })
            .finally(() => {
                isAdding = false;
                button.disabled = false;
                offer?.classList.remove('is-adding');
            });
    };

    /* ------------------------- ORPHAN FILTER CLEANUP ------------------------- */
    // If a shopper removes a trigger product via the native delete icon,
    // that test's hidden subscription line is now orphaned and must be
    // removed. Checked independently per test since each test has its own
    // filter product now.

    let orphanFilterCleanupTimer = null;
    let isRemovingOrphanFilter = false;

    const shouldRemoveOrphanFilterForTest = (cart, test) => {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        if (!items.length || !isFilterSubscribedForTest(cart, test)) return false;
        return !items.some((item) => isTriggerCartItem(item, test));
    };

    const getOrphanFilterTests = (cart) => TESTS.filter((test) => shouldRemoveOrphanFilterForTest(cart, test));

    const scheduleOrphanFilterCleanup = (cart) => {
        window.clearTimeout(orphanFilterCleanupTimer);
        if (isAdding || isRemovingOrphanFilter) return;

        const orphanTests = getOrphanFilterTests(cart);
        if (!orphanTests.length) return;

        orphanFilterCleanupTimer = window.setTimeout(() => {
            if (isAdding || isRemovingOrphanFilter) return;

            isRemovingOrphanFilter = true;
            fetchFreshCart()
                .then((freshCart) => {
                    const stillOrphaned = getOrphanFilterTests(freshCart);
                    if (!stillOrphaned.length) return freshCart;

                    debugLog('orphan cleanup: removing hidden filter subscription line(s) for', stillOrphaned.map((t) => t.key));

                    return stillOrphaned.reduce(
                        (chain, test) => chain.then((currentCart) => (
                            removeSubscriptionFromCart(currentCart, test).then(afterSubscriptionCartChange)
                        )),
                        Promise.resolve(freshCart)
                    );
                })
                .catch((error) => {
                    debugLog('orphan filter cleanup failed', error);
                })
                .finally(() => {
                    isRemovingOrphanFilter = false;
                });
        }, 650);
    };

    /* -------------------------------- RENDER -------------------------------- */

    const renderTimers = new WeakMap();
    const activeRoots = new WeakSet();

    const renderTestOnRoot = (test, cart, rootDoc) => {
        const domState = getRootDomStateForTest(rootDoc, test);

        if (!cart && !domState.hasTriggerProduct && !domState.hasFilterProduct) {
            removeOffers(test, false, rootDoc);
            return;
        }

        const state = cart ? getCartStateForTest(cart, test) : { hasTriggerProduct: false, hasFilterProduct: false, hasExcludedProduct: false };
        state.hasTriggerProduct = state.hasTriggerProduct || domState.hasTriggerProduct;
        if (!cart) state.hasFilterProduct = state.hasFilterProduct || domState.hasFilterProduct;
        state.hasExcludedProduct = state.hasExcludedProduct || domState.hasExcludedProduct;

        // Excluded product only hides the offer if this test's own filter
        // isn't already subscribed — an already-active subscription should
        // stay visible/toggleable even if an excluded product later enters.
        if ((!state.hasTriggerProduct && !state.hasFilterProduct) || (state.hasExcludedProduct && !state.hasFilterProduct)) {
            removeOffers(test, false, rootDoc);
            return;
        }

        const targets = getInsertionTargets(rootDoc);
        if (!targets.length) {
            removeOffers(test, false, rootDoc);
            return;
        }

        removeStaleOffers(targets, test, rootDoc);
        fetchProductDataForTest(test).then((productDataResponse) => {
            const product = productDataResponse || getFallbackProductData(test);
            const variant = getProductVariantForTest(product, test);
            if (!product?.available || !variant) {
                removeOffers(test, false, rootDoc);
                return;
            }
            insertOffers(targets, test, product, state.hasFilterProduct);
        });
    };

    const renderAllTestsOnRoot = (rootDoc = document) => {
        if (isAdding) return;

        fetchCart().then((cart) => {
            scheduleOrphanFilterCleanup(cart);
            TESTS.forEach((test) => renderTestOnRoot(test, cart, rootDoc));
        });
    };

    const queueRenderOffer = (rootDoc = document) => {
        window.clearTimeout(renderTimers.get(rootDoc));
        renderTimers.set(rootDoc, window.setTimeout(() => renderAllTestsOnRoot(rootDoc), 80));
    };

    function queueRenderAllRoots() {
        setupFrameLoadListeners();
        getRootDocuments().forEach((rootDoc) => {
            setupRoot(rootDoc);
            queueRenderOffer(rootDoc);
        });
    }

    const discoverCartRoots = () => {
        setupFrameLoadListeners();
        getRootDocuments().forEach((rootDoc) => setupRoot(rootDoc));
    };

    const cartMutationSelectors = [
        '.kaktusc-cart__products', '.kaktusc-cart__product',
        '#CartDrawer-CartItems', '#main-cart-items',
        'cart-drawer', '#CartDrawer', 'iframe#kaktusc-widget'
    ].join(',');

    const isRelevantCartMutationNode = (node) => {
        if (!node || node.nodeType !== 1) return false;
        if (TESTS.some((test) => node.closest?.(`.${test.offerClass}`))) return false;
        return Boolean(node.matches?.(cartMutationSelectors) || node.closest?.(cartMutationSelectors) || node.querySelector?.(cartMutationSelectors));
    };

    const isRootDiscoveryMutationNode = (node) => {
        if (!node || node.nodeType !== 1) return false;
        return Boolean(node.matches?.('iframe#kaktusc-widget, iframe') || node.querySelector?.('iframe#kaktusc-widget, iframe'));
    };

    function setupRoot(rootDoc = document) {
        if (!rootDoc?.documentElement || !rootDoc.body || activeRoots.has(rootDoc)) return;
        activeRoots.add(rootDoc);
        injectRootStyle(rootDoc);

        const observer = new MutationObserver((mutations) => {
            const shouldRender = mutations.some((mutation) => {
                const added = Array.from(mutation.addedNodes || []);
                const removed = Array.from(mutation.removedNodes || []);
                return isRelevantCartMutationNode(mutation.target) || [...added, ...removed].some(isRelevantCartMutationNode);
            });

            if (!shouldRender) return;

            const shouldRefreshRoots = rootDoc === document && mutations.some((mutation) => {
                const added = Array.from(mutation.addedNodes || []);
                return isRootDiscoveryMutationNode(mutation.target) || added.some(isRootDiscoveryMutationNode);
            });

            if (shouldRefreshRoots) { queueRenderAllRoots(); return; }
            queueRenderOffer(rootDoc);
        });
        observer.observe(rootDoc.documentElement, { childList: true, subtree: true });

        rootDoc.addEventListener('click', (event) => {
            const button = event.target.closest?.('.bello-subscription-toggle');
            if (!button) return;
            const test = TESTS.find((t) => t.key === button.dataset.testKey);
            if (!test) return;
            event.preventDefault();
            toggleSubscriptionInCart(button, test);
        });

        queueRenderOffer(rootDoc);
    }

    /* --------------------------------- BOOT ---------------------------------- */

    let isSetup = false;
    let rootDiscoveryInterval = null;
    let rootDiscoveryAttempts = 0;

    const setupListeners = () => {
        ['cart:refresh', 'cart:updated', 'cart:change', 'dispatch:cart-drawer:refresh'].forEach((eventName) => {
            document.addEventListener(eventName, () => window.setTimeout(queueRenderAllRoots, 250));
        });

        TESTS.forEach((test) => {
            fetchProductDataForTest(test).then((product) => {
                const availablePlanIds = (product?.selling_plan_groups?.[0]?.selling_plans || []).map((p) => Number(p.id));
                if (test.sellingPlanId && !availablePlanIds.includes(Number(test.sellingPlanId))) {
                    debugLog(`⚠️ Configured selling plan for "${test.key}" (${test.sellingPlanId}) not found on product — subscriptions may fail`);
                }
            });
        });

        setupCartRequestObserver();
        queueRenderAllRoots();

        rootDiscoveryInterval = window.setInterval(() => {
            rootDiscoveryAttempts += 1;
            discoverCartRoots();
            if (rootDiscoveryAttempts > 60) window.clearInterval(rootDiscoveryInterval);
        }, 500);
    };

    const loadTest = () => {
        if (isSetup) return;
        isSetup = true;
        debugLog('Bello filter upsell (dual product: be09 + be04, locale-aware) — Load', { locale: getLocale() });
        setupListeners();
    };

    waitForElement('body').then(loadTest);
})();

